import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ArrowRight, FileUp, Loader2, Plus, Trash2 } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/atoms/card";
import { Button } from "~/atoms/button";
import { type Option } from "~/molecules/ControlledCombobox";
import { getFileExtension } from "~/helpers/file";
import {
  intakeFiles,
  patchRows,
  markRowsCreating,
  markRowCreated,
  markFailedAndMoveToTop,
  isDraftRowValid,
  type DraftRow,
  type RowPatch,
  type FileIntakeResult,
} from "~/helpers/draftRows";
import { UPLOAD_FILE_TYPES_ACCEPT } from "~/api/v2/constants";
import { useDropZone } from "~/hooks/useDropZone";
import { useBulkSelection } from "~/hooks/useBulkSelection";
import { useTiers } from "~/api/swr/useTiers";
import { useCategories } from "~/api/swr/useCategories";
import { mapTierOptions, tierIdFormValueToNumberOrNull } from "~/helpers/tier";
import { useDocumentReleases } from "~/api/v2/useDocumentReleases";
import { createDocument } from "~/api/v2/requests";
import { OnboardFooter } from "~/molecules/onboard-documents/OnboardFooter";
import { DraftsTable } from "~/molecules/onboard-documents/DraftsTable";
import { StickyHorizontalScrollbar } from "~/molecules/StickyHorizontalScrollbar";
import { UnsavedChangesAlert } from "~/molecules/UnsavedChangesAlert";
import { cn } from "~/lib/utils";

interface Props {
  onCreated: (releaseId: number) => void;
  onNext: () => void;
}

export const CreateDraftsStep = ({ onCreated, onNext }: Props) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const [rows, setRows] = useState<DraftRow[]>([]);
  const [isCreating, setIsCreating] = useState(false);

  const eligibleIds = rows
    .filter((r) => r.status !== "creating" && r.status !== "created")
    .map((r) => r.id);

  const {
    selectedIds,
    allSelected,
    toggleRow,
    toggleAll,
    deselect,
    clearSelection,
    bulkTargets,
  } = useBulkSelection(eligibleIds);

  const createdCount = rows.filter((r) => r.status === "created").length;
  const hasUncreated = rows.length - createdCount > 0;
  const validSelectedCount = rows.filter(
    (r) => selectedIds.has(r.id) && isDraftRowValid(r),
  ).length;

  const { data: tiers } = useTiers();
  const { data: categories } = useCategories();
  const { data: releases } = useDocumentReleases();

  const tierOptions = useMemo<Option[] | undefined>(
    () => mapTierOptions(tiers, t),
    [tiers, t],
  );

  const categoryOptions = useMemo<Option[] | undefined>(
    () =>
      categories?.map((cat) => ({
        label: cat.title,
        value: cat.id.toString(),
      })),
    [categories],
  );

  const showIntakeToasts = ({ rejected, duplicateCount }: FileIntakeResult) => {
    if (duplicateCount > 0) {
      toast.warning(
        t(($) => $.library.onboardDocuments.steps.createDrafts.duplicateFiles),
      );
    }
    for (const r of rejected) {
      const message =
        r.reason === "unsupported_type"
          ? t(
              ($) =>
                $.library.onboardDocuments.steps.createDrafts.unsupportedType,
              { name: r.name },
            )
          : t(
              ($) => $.library.onboardDocuments.steps.createDrafts.fileTooLarge,
              { name: r.name },
            );
      toast.error(message);
    }
  };

  const onPickFiles = (incoming: FileList | null) => {
    const result = intakeFiles(incoming, rows);
    if (result.newRows.length > 0) {
      setRows((prev) => [...prev, ...result.newRows]);
    }
    showIntakeToasts(result);
    if (inputRef.current) inputRef.current.value = "";
  };

  const { isDragging, dropZoneProps } = useDropZone({ onDrop: onPickFiles });

  const handleUpdateRow = (
    id: string,
    patch: RowPatch,
    opts?: { bulk?: boolean },
  ) => {
    const ids = opts?.bulk ? bulkTargets(id) : new Set([id]);
    setRows((prev) => patchRows(prev, ids, patch));
  };

  const handleRemoveRow = (id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
    deselect([id]);
  };

  const handleClearSelected = () => {
    setRows((prev) => prev.filter((r) => !selectedIds.has(r.id)));
    clearSelection();
  };

  const createOne = async (row: DraftRow): Promise<boolean> => {
    const formData = new FormData();
    formData.append("name", row.title.trim());
    const tierId = tierIdFormValueToNumberOrNull(row.tierId!);
    formData.append("tierId", tierId === null ? "" : tierId.toString());
    formData.append("categoryId", row.categoryId!);
    formData.append("fileType", getFileExtension(row.file));
    formData.append("file", row.file);

    try {
      const { id } = await createDocument(formData);
      onCreated(id);
      return true;
    } catch {
      return false;
    }
  };

  const runCreate = async (toCreate: DraftRow[]) => {
    setIsCreating(true);
    const creatingIds = new Set(toCreate.map((r) => r.id));
    setRows((prev) => markRowsCreating(prev, creatingIds));

    const successIds: string[] = [];
    const failedIds: string[] = [];
    for (const row of toCreate) {
      const ok = await createOne(row);
      if (ok) {
        successIds.push(row.id);
        setRows((prev) => markRowCreated(prev, row.id));
      } else {
        failedIds.push(row.id);
      }
    }

    if (failedIds.length > 0) {
      setRows((prev) => markFailedAndMoveToTop(prev, new Set(failedIds)));
    }

    setIsCreating(false);

    deselect(successIds);

    if (successIds.length > 0) {
      toast.success(
        t(
          ($) =>
            $.library.onboardDocuments.steps.createDrafts.toasts.createdSome,
          { count: successIds.length },
        ),
      );
    }
    if (failedIds.length > 0) {
      toast.error(
        t(
          ($) =>
            $.library.onboardDocuments.steps.createDrafts.toasts.failedSome,
          { count: failedIds.length },
        ),
      );
    }
  };

  const handleCreateSelected = () =>
    runCreate(rows.filter((r) => selectedIds.has(r.id) && isDraftRowValid(r)));

  const handleCreateOne = (row: DraftRow) => {
    if (isDraftRowValid(row)) void runCreate([row]);
  };

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>
            {t(($) => $.library.onboardDocuments.steps.createDrafts.cardTitle)}
          </CardTitle>
          <CardDescription>
            {t(
              ($) =>
                $.library.onboardDocuments.steps.createDrafts.cardDescription,
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={UPLOAD_FILE_TYPES_ACCEPT}
            className="hidden"
            onChange={(e) => onPickFiles(e.target.files)}
          />
          {rows.length === 0 ? (
            <div
              className={cn(
                "border-border flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed p-8 text-center",
                isDragging ? "bg-muted/50" : "hover:bg-muted/50",
              )}
              onClick={() => inputRef.current?.click()}
              {...dropZoneProps}
            >
              <FileUp className="text-muted-foreground size-8" />
              <p className="text-base">
                {t(
                  ($) =>
                    $.library.onboardDocuments.steps.createDrafts.clickOrDrag,
                )}
              </p>
            </div>
          ) : (
            <div
              className={cn(
                "rounded-lg border-2 border-dashed p-1",
                isDragging ? "border-border bg-muted/50" : "border-transparent",
              )}
              {...dropZoneProps}
            >
              <DraftsTable
                wrapperRef={tableWrapperRef}
                rows={rows}
                selectedIds={selectedIds}
                allSelected={allSelected}
                isCreating={isCreating}
                tierOptions={tierOptions}
                categoryOptions={categoryOptions}
                releases={releases}
                createdCount={createdCount}
                onToggleAll={toggleAll}
                onToggleRow={toggleRow}
                onUnselectAll={clearSelection}
                onUpdateRow={handleUpdateRow}
                onCreateRow={handleCreateOne}
                onRemoveRow={handleRemoveRow}
              />
            </div>
          )}
        </CardContent>
      </Card>
      <StickyHorizontalScrollbar
        targetRef={tableWrapperRef}
        deps={[rows]}
        className="sticky bottom-12 h-4"
      />
      {rows.length > 0 && (
        <OnboardFooter
          left={
            <Button
              variant="outline"
              onClick={() => inputRef.current?.click()}
              disabled={isCreating}
            >
              <Plus className="size-4" />
              {t(($) => $.library.onboardDocuments.steps.createDrafts.addMore)}
            </Button>
          }
          right={
            <>
              {selectedIds.size > 0 && (
                <Button
                  variant="destructive"
                  onClick={handleClearSelected}
                  disabled={isCreating}
                >
                  <Trash2 className="size-4" />
                  {t(
                    ($) =>
                      $.library.onboardDocuments.steps.createDrafts
                        .clearSelected,
                    { count: selectedIds.size },
                  )}
                </Button>
              )}
              {validSelectedCount > 0 && (
                <Button onClick={handleCreateSelected} disabled={isCreating}>
                  {isCreating && <Loader2 className="animate-spin" />}
                  {t(
                    ($) =>
                      $.library.onboardDocuments.steps.createDrafts.createCount,
                    { count: validSelectedCount },
                  )}
                </Button>
              )}
              {!hasUncreated && (
                <Button variant="outline" onClick={onNext}>
                  {t(($) => $.library.onboardDocuments.steps.createDrafts.next)}
                  <ArrowRight className="size-4" />
                </Button>
              )}
            </>
          }
        />
      )}
      {hasUncreated ? (
        <UnsavedChangesAlert hasUnsavedChanges />
      ) : (
        <UnsavedChangesAlert
          hasUnsavedChanges={createdCount > 0}
          title={t(($) => $.library.onboardDocuments.leaveAlert.title)}
          description={t(
            ($) => $.library.onboardDocuments.leaveAlert.draftsDescription,
          )}
          cancelLabel={t(($) => $.library.onboardDocuments.leaveAlert.stay)}
          confirmLabel={t(($) => $.library.onboardDocuments.leaveAlert.leave)}
        />
      )}
    </div>
  );
};
